import React, { useState, useEffect, useRef } from "react";
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Spinner,
    Row,
    Col,
    Nav,
    NavItem,
    NavLink
} from "reactstrap";
import { getWithdrawalHistory, getUserTransactions } from "../../helpers/apis";
import { StatCard } from "../../Components/Common/StatCard";
import { toast } from "react-toastify";
import classnames from "classnames";
import CommonDataTable from "../../common/DataTable";
import { formatDateTimeDDMMYYYY } from "../../helpers/dateUtils";

const WithdrawalHistoryModal = ({ isOpen, toggle, user }) => {
    const [activeTab, setActiveTab] = useState('1'); // '1' for Withdrawal, '2' for Statement
    const [historyData, setHistoryData] = useState(null);
    const [statementData, setStatementData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [statementLoading, setStatementLoading] = useState(false);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0
    });
    const [statementPagination, setStatementPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0
    });
    const [filters, setFilters] = useState({
        page: 1,
        limit: 20
    });
    const [statementFilters, setStatementFilters] = useState({
        page: 1,
        limit: 20,
        type: 'all'
    });
    const isMounted = useRef(true);

    const userId = user?._id || user?.id;

    const fetchHistory = async (params = {}) => {
        if (!userId) return;

        setLoading(true);
        try {
            const requestParams = { 
                page: params.page || filters.page || 1,
                limit: params.limit || filters.limit || 20
            };
            const response = await getWithdrawalHistory(userId, requestParams);

            // The axios interceptor returns response.data directly, so response is already the data object
            if (response && response.status === 1) {
                setHistoryData(response.data);
                const paginationData = response.data?.pagination || {};
                const newPagination = {
                    page: paginationData.page || requestParams.page || 1,
                    limit: paginationData.limit || requestParams.limit || 20,
                    total: paginationData.total || 0,
                    totalPages: paginationData.totalPages || 1,
                    hasNextPage: paginationData.hasNextPage || false,
                    hasPrevPage: paginationData.hasPrevPage || false
                };
                setPagination(newPagination);
                // Update filters to match pagination
                setFilters({
                    page: newPagination.page,
                    limit: newPagination.limit
                });
            } else {
                toast.error(response?.message || "Failed to fetch history");
            }
        } catch (error) {
            console.error("Error fetching withdrawal history:", error);
            toast.error(error.message || "Failed to fetch history");
        } finally {
            setLoading(false);
        }
    };

    const fetchStatement = async (params = {}) => {
        if (!userId) return;

        setStatementLoading(true);
        try {
            const requestParams = {
                page: params.page || statementFilters.page || 1,
                limit: params.limit || statementFilters.limit || 20
            };
            const response = await getUserTransactions(userId, requestParams);

            if (response && response.status === 1) {
                // The axios interceptor returns response.data directly, so response is already the data object
                // API response structure: { statusCode, status, message, data: { transactions, pagination, summary, ... } }
                const data = response.data || response;
                
                // Set the data - use the nested data if it exists, otherwise use the response directly
                const finalData = data?.data || data;
                setStatementData(finalData);
                
                const paginationData = finalData?.pagination || data?.pagination || {};
                const newPagination = {
                    page: paginationData.page || requestParams.page || 1,
                    limit: paginationData.limit || requestParams.limit || 20,
                    total: paginationData.total || 0,
                    totalPages: paginationData.totalPages || 1,
                    hasNextPage: paginationData.hasNextPage || false,
                    hasPrevPage: paginationData.hasPrevPage || false
                };
                setStatementPagination(newPagination);
                // Update filters to match pagination
                setStatementFilters({
                    page: newPagination.page,
                    limit: newPagination.limit,
                    type: 'all'
                });
            } else {
                toast.error(response?.message || "Failed to fetch statement");
            }
        } catch (error) {
            console.error("Error fetching statement:", error);
            // Handle specific error messages
            const errorMessage = error.message || "Failed to fetch statement";
            if (errorMessage.includes("Reduce of empty array")) {
                toast.error("Server error: Unable to process statement data. Please try again.");
            } else {
                toast.error(errorMessage);
            }
            setStatementData(null);
        } finally {
            setStatementLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen && userId) {
            if (activeTab === '1') {
                fetchHistory();
            } else if (activeTab === '2') {
                fetchStatement();
            }
        }
    }, [isOpen, userId, activeTab]);

    useEffect(() => {
        return () => {
            isMounted.current = false;
        };
    }, []);

    const handlePageChange = (newPage) => {
        const newFilters = { ...filters, page: newPage };
        setFilters(newFilters);
        fetchHistory(newFilters);
    };

    const handleRowsPerPageChange = (newLimit) => {
        const newFilters = { ...filters, page: 1, limit: newLimit };
        setFilters(newFilters);
        fetchHistory(newFilters);
    };

    // Generate page numbers for pagination
    const getPageNumbers = () => {
        const pages = [];
        const totalPages = pagination.totalPages || 1;
        const currentPage = pagination.page || 1;
        
        if (totalPages <= 7) {
            // Show all pages if 7 or fewer
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            // Show first page, last page, current page, and pages around current
            if (currentPage <= 3) {
                // Near the start
                for (let i = 1; i <= 5; i++) {
                    pages.push(i);
                }
                pages.push('...');
                pages.push(totalPages);
            } else if (currentPage >= totalPages - 2) {
                // Near the end
                pages.push(1);
                pages.push('...');
                for (let i = totalPages - 4; i <= totalPages; i++) {
                    pages.push(i);
                }
            } else {
                // In the middle
                pages.push(1);
                pages.push('...');
                for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                    pages.push(i);
                }
                pages.push('...');
                pages.push(totalPages);
            }
        }
        return pages;
    };

    const handleClose = () => {
        setHistoryData(null);
        setStatementData(null);
        setActiveTab('1');
        setFilters({ page: 1, limit: 20 });
        setStatementFilters({ page: 1, limit: 20, type: 'all' });
        toggle();
    };

    const handleStatementPageChange = (newPage) => {
        const newFilters = { ...statementFilters, page: newPage };
        setStatementFilters(newFilters);
        fetchStatement(newFilters);
    };

    const handleStatementRowsPerPageChange = (newLimit) => {
        const newFilters = { ...statementFilters, page: 1, limit: newLimit };
        setStatementFilters(newFilters);
        fetchStatement(newFilters);
    };

    const getStatementPageNumbers = () => {
        const pages = [];
        const totalPages = statementPagination.totalPages || 1;
        const currentPage = statementPagination.page || 1;
        
        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            if (currentPage <= 3) {
                for (let i = 1; i <= 5; i++) {
                    pages.push(i);
                }
                pages.push('...');
                pages.push(totalPages);
            } else if (currentPage >= totalPages - 2) {
                pages.push(1);
                pages.push('...');
                for (let i = totalPages - 4; i <= totalPages; i++) {
                    pages.push(i);
                }
            } else {
                pages.push(1);
                pages.push('...');
                for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                    pages.push(i);
                }
                pages.push('...');
                pages.push(totalPages);
            }
        }
        return pages;
    };

    // Calculate summary statistics
    const calculateSummary = () => {
        if (!historyData?.transactions || !Array.isArray(historyData.transactions)) {
            return {
                totalCoins: user?.currentCoins || 0,
                totalAdd: 0,
                totalRemove: 0
            };
        }

        const transactions = historyData.transactions || [];
        const totalAdd = transactions
            .filter(t => t && t.type === 'admin_add' && t.isCredit)
            .reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0);
        
        const totalRemove = transactions
            .filter(t => t && (t.type === 'admin_remove' || (!t.isCredit && t.type !== 'admin_add')))
            .reduce((sum, t) => sum + (Math.abs(parseFloat(t.amount)) || 0), 0);

        return {
            totalCoins: user?.currentCoins || 0,
            totalAdd,
            totalRemove
        };
    };

    const summary = calculateSummary();
    const transactions = historyData?.transactions || [];

    const formatDate = (dateString) => {
        return formatDateTimeDDMMYYYY(dateString);
    };

    // Column definitions for Withdrawal tab
    const withdrawalColumns = [
        {
            name: "Type",
            cell: (row) => {
                const isAdd = row.type === 'admin_add' && row.isCredit;
                const isRemove = row.type === 'admin_remove' || (!row.isCredit && row.type !== 'admin_add');
                return (
                    <>
                        {isAdd ? (
                            <span className="badge bg-success">
                                <i className="ri-add-line"></i>
                            </span>
                        ) : isRemove ? (
                            <span className="badge bg-danger">
                                <i className="ri-subtract-line"></i>
                            </span>
                        ) : (
                            <span className="badge bg-secondary">
                                {row.type}
                            </span>
                        )}
                    </>
                );
            },
            width: "10%"
        },
        {
            name: "Description",
            selector: (row) => row.description || 'N/A',
            sortable: true,
            width: "30%"
        },
        {
            name: "Add",
            cell: (row) => {
                const isAdd = row.type === 'admin_add' && row.isCredit;
                const amount = row.amount || 0;
                return isAdd ? (
                    <span className="text-success fw-semibold">
                        +{amount.toLocaleString()}
                    </span>
                ) : (
                    <span className="text-muted">-</span>
                );
            },
            width: "15%"
        },
        {
            name: "Remove",
            cell: (row) => {
                const isRemove = row.type === 'admin_remove' || (!row.isCredit && row.type !== 'admin_add');
                const amount = row.amount || 0;
                return isRemove ? (
                    <span className="text-danger fw-semibold">
                        -{amount.toLocaleString()}
                    </span>
                ) : (
                    <span className="text-muted">-</span>
                );
            },
            width: "15%"
        },
        {
            name: "Time",
            cell: (row) => (
                <small className="text-muted">
                    {formatDate(row.timestamp)}
                </small>
            ),
            sortable: true,
            width: "30%"
        }
    ];

    // Column definitions for Statement tab
    const statementColumns = [
        {
            name: "Description",
            cell: (row) => row.description || 'N/A',
            selector: (row) => row.description || 'N/A',
            sortable: true,
            width: "40%"
        },
        {
            name: "Add",
            cell: (row) => {
                // Use isCredit to determine if it's an add transaction
                const isAdd = row.isCredit === true;
                // Use add field if available, otherwise use amount
                const amount = row.add || (isAdd ? Math.abs(row.amount || 0) : 0);
                return isAdd && amount > 0 ? (
                    <span className="text-success fw-semibold">
                        +{amount.toLocaleString()}
                    </span>
                ) : (
                    <span className="text-muted">-</span>
                );
            },
            width: "20%"
        },
        {
            name: "Remove",
            cell: (row) => {
                // Use isCredit to determine if it's a remove transaction
                const isRemove = row.isCredit === false;
                // Use remove field if available, otherwise use amount
                const amount = row.remove || (isRemove ? Math.abs(row.amount || 0) : 0);
                return isRemove && amount > 0 ? (
                    <span className="text-danger fw-semibold">
                        -{amount.toLocaleString()}
                    </span>
                ) : (
                    <span className="text-muted">-</span>
                );
            },
            width: "20%"
        },
        {
            name: "Time",
            cell: (row) => (
                <small className="text-muted">
                    {formatDate(row.timestamp)}
                </small>
            ),
            sortable: true,
            width: "20%"
        }
    ];

    return (
        <Modal
            isOpen={isOpen}
            toggle={handleClose}
            size="xl"
            scrollable
            className="modal-fullscreen-lg-down modal-fixed-header-footer"
        >
            <ModalHeader toggle={handleClose} className="bg-white border-0 pb-2">
                <div className="d-flex flex-column w-100">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h5 className="mb-0 fw-semibold">
                            <i className="ri-history-line me-2 text-primary"></i>
                            Withdrawal History
                        </h5>
                    </div>
                </div>
            </ModalHeader>

            <ModalBody className="p-0" style={{ backgroundColor: '#f8f9fa' }}>
                {/* Tabs Navigation */}
                <Nav tabs className="px-3 pt-3 bg-white">
                    <NavItem>
                        <NavLink
                            className={classnames({ active: activeTab === '1' })}
                            onClick={() => setActiveTab('1')}
                            style={{
                                cursor: 'pointer',
                                borderRadius: '8px 8px 0 0',
                                fontWeight: activeTab === '1' ? '600' : '400'
                            }}
                        >
                            <i className="ri-wallet-3-line me-2"></i>
                            Withdrawal
                        </NavLink>
                    </NavItem>
                    <NavItem>
                        <NavLink
                            className={classnames({ active: activeTab === '2' })}
                            onClick={() => setActiveTab('2')}
                            style={{
                                cursor: 'pointer',
                                borderRadius: '8px 8px 0 0',
                                fontWeight: activeTab === '2' ? '600' : '400'
                            }}
                        >
                            <i className="ri-file-list-3-line me-2"></i>
                            Statement
                        </NavLink>
                    </NavItem>
                </Nav>

                {/* Tab Content */}
                <div className="p-3 bg-white">
                    {activeTab === '1' && (
                        <>
                            {loading ? (
                                <div className="text-center py-5">
                                    <Spinner color="primary" className="mb-3" style={{ width: '3rem', height: '3rem' }} />
                                    <p className="text-muted">Loading withdrawal history...</p>
                                </div>
                            ) : (
                                <>
                                    {/* Summary Cards */}
                                    <div className="mb-3">
                                        <Row>
                                            <Col md={4} sm={6}>
                                                <StatCard
                                                    icon="ri-wallet-3-line"
                                                    title="Total Coins"
                                                    value={summary.totalCoins}
                                                    color="info"
                                                    iconBg="#e7f3ff"
                                                />
                                            </Col>
                                            <Col md={4} sm={6}>
                                                <StatCard
                                                    icon="ri-add-circle-line"
                                                    title="Total Added"
                                                    value={summary.totalAdd}
                                                    color="success"
                                                    iconBg="#d4edda"
                                                />
                                            </Col>
                                            <Col md={4} sm={6}>
                                                <StatCard
                                                    icon="ri-subtract-line"
                                                    title="Total Removed"
                                                    value={summary.totalRemove}
                                                    color="danger"
                                                    iconBg="#ffe4e1"
                                                />
                                            </Col>
                                        </Row>
                                    </div>

                                    {/* Transactions Table */}
                                    <div>
                                        <h6 className="mb-3 fw-semibold">
                                            <i className="ri-file-list-3-line me-2 text-primary"></i>
                                            Transaction History
                                        </h6>
                                        <CommonDataTable
                                            columns={withdrawalColumns}
                                            data={transactions}
                                            loading={loading}
                                            totalRows={pagination.total || 0}
                                            filterParams={filters}
                                            fetchData={(newParams) => {
                                                if (userId) {
                                                    // Handle both limit and perPage from CommonDataTable
                                                    const updatedFilters = { 
                                                        ...filters, 
                                                        ...newParams,
                                                        limit: newParams.limit || newParams.perPage || filters.limit || 20,
                                                        page: newParams.page || newParams.currentPage || filters.page || 1
                                                    };
                                                    setFilters(updatedFilters);
                                                    fetchHistory(updatedFilters);
                                                }
                                            }}
                                            cardcusclass="mt-0 mb-0"
                                            callcusclass="p-0 mt-0"
                                            searchEnable={false}
                                            showAddButton={false}
                                            showExportButton={false}
                                            checkboxEnabled={false}
                                            paginationPerPage={filters.limit || 20}
                                            paginationDefaultPage={filters.page || 1}
                                            onChangePage={handlePageChange}
                                            onChangeRowsPerPage={handleRowsPerPageChange}
                                            striped
                                            highlightOnHover
                                            noHeader
                                        />
                                    </div>
                                </>
                            )}
                        </>
                    )}

                    {activeTab === '2' && (
                        <>
                            {statementLoading ? (
                                <div className="text-center py-5">
                                    <Spinner color="primary" className="mb-3" style={{ width: '3rem', height: '3rem' }} />
                                    <p className="text-muted">Loading statement...</p>
                                </div>
                            ) : (
                                <>
                                    {/* Statement Summary Cards */}
                                    {statementData?.summary && (
                                        <div className="mb-3">
                                            <Row>
                                                <Col md={3} sm={6}>
                                                    <StatCard
                                                        icon="ri-file-list-3-line"
                                                        title="Total Transactions"
                                                        value={statementData.summary.totalTransactions || 0}
                                                        color="info"
                                                        iconBg="#e7f3ff"
                                                    />
                                                </Col>
                                                <Col md={3} sm={6}>
                                                    <StatCard
                                                        icon="ri-gamepad-line"
                                                        title="Lucky Target"
                                                        value={statementData.summary.totalInLuckyTarget || 0}
                                                        color="primary"
                                                        iconBg="#e7f3ff"
                                                    />
                                                </Col>
                                                <Col md={3} sm={6}>
                                                    <StatCard
                                                        icon="ri-gamepad-fill"
                                                        title="Lucky Target 1"
                                                        value={statementData.summary.totalInLuckyTarget1 || 0}
                                                        color="primary"
                                                        iconBg="#e7f3ff"
                                                    />
                                                </Col>
                                                <Col md={3} sm={6}>
                                                    <StatCard
                                                        icon="ri-wallet-3-line"
                                                        title="Net Amount"
                                                        value={statementData.summary.totalAmount?.net || 0}
                                                        color={statementData.summary.totalAmount?.net >= 0 ? "success" : "danger"}
                                                        iconBg={statementData.summary.totalAmount?.net >= 0 ? "#d4edda" : "#ffe4e1"}
                                                    />
                                                </Col>
                                            </Row>
                                        </div>
                                    )}
                                    
                                    {/* Transaction Type Summary */}
                                    {statementData?.summary?.byType && (
                                        <div className="mb-3">
                                            <Row>
                                                <Col md={3} sm={6}>
                                                    <StatCard
                                                        icon="ri-add-circle-line"
                                                        title="Admin Add"
                                                        value={statementData.summary.byType.admin_add || 0}
                                                        color="success"
                                                        iconBg="#d4edda"
                                                    />
                                                </Col>
                                                <Col md={3} sm={6}>
                                                    <StatCard
                                                        icon="ri-subtract-line"
                                                        title="Admin Remove"
                                                        value={statementData.summary.byType.admin_remove || 0}
                                                        color="danger"
                                                        iconBg="#ffe4e1"
                                                    />
                                                </Col>
                                                <Col md={3} sm={6}>
                                                    <StatCard
                                                        icon="ri-arrow-down-circle-line"
                                                        title="Total Credits"
                                                        value={statementData.summary.totalAmount?.credits || 0}
                                                        color="success"
                                                        iconBg="#d4edda"
                                                    />
                                                </Col>
                                                <Col md={3} sm={6}>
                                                    <StatCard
                                                        icon="ri-arrow-up-circle-line"
                                                        title="Total Debits"
                                                        value={statementData.summary.totalAmount?.debits || 0}
                                                        color="danger"
                                                        iconBg="#ffe4e1"
                                                    />
                                                </Col>
                                            </Row>
                                        </div>
                                    )}

                                    {/* Statement Transactions Table */}
                                    <div>
                                        <h6 className="mb-3 fw-semibold">
                                            <i className="ri-file-list-3-line me-2 text-primary"></i>
                                            All Transactions
                                        </h6>
                                        {(() => {
                                            // Get transactions from the correct path
                                            // API response has transactions directly in data.transactions
                                            const statementTransactions = statementData?.transactions || 
                                                                        statementData?.detailedHistory?.recentTransactions || 
                                                                        statementData?.data?.detailedHistory?.recentTransactions ||
                                                                        statementData?.recentTransactions || 
                                                                        statementData?.data?.recentTransactions ||
                                                                        [];
                                            
                                            return (
                                                <CommonDataTable
                                                    columns={statementColumns}
                                                    data={statementTransactions}
                                                    loading={statementLoading}
                                                    totalRows={statementPagination.total || 0}
                                                    filterParams={statementFilters}
                                                    fetchData={(newParams) => {
                                                        if (userId) {
                                                            // Handle both limit and perPage from CommonDataTable
                                                            const updatedFilters = { 
                                                                ...statementFilters, 
                                                                ...newParams,
                                                                limit: newParams.limit || newParams.perPage || statementFilters.limit || 20,
                                                                page: newParams.page || newParams.currentPage || statementFilters.page || 1
                                                            };
                                                            setStatementFilters(updatedFilters);
                                                            fetchStatement(updatedFilters);
                                                        }
                                                    }}
                                                    cardcusclass="mt-0 mb-0"
                                                    callcusclass="p-0 mt-0"
                                                    searchEnable={false}
                                                    showAddButton={false}
                                                    showExportButton={false}
                                                    checkboxEnabled={false}
                                                    paginationPerPage={statementFilters.limit || 20}
                                                    paginationDefaultPage={statementFilters.page || 1}
                                                    onChangePage={handleStatementPageChange}
                                                    onChangeRowsPerPage={handleStatementRowsPerPageChange}
                                                    striped
                                                    highlightOnHover
                                                    noHeader
                                                />
                                            );
                                        })()}
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </div>
            </ModalBody>

            <ModalFooter className="bg-white border-0">
                <div className="d-flex justify-content-between w-100 align-items-center">
                    <div className="text-muted">
                        <small>
                            <i className="ri-time-line me-1"></i>
                            {activeTab === '1' && historyData && transactions.length > 0 && (
                                <>Last Transaction: {formatDate(transactions[0]?.timestamp)}</>
                            )}
                            {activeTab === '2' && statementData && (() => {
                                const statementTransactions = statementData?.transactions || 
                                                             statementData?.detailedHistory?.recentTransactions || 
                                                             statementData?.recentTransactions || [];
                                return statementTransactions.length > 0 && (
                                    <>Last Transaction: {formatDate(statementTransactions[0]?.timestamp)}</>
                                );
                            })()}
                        </small>
                    </div>
                    <div className="d-flex gap-2">
                        <Button color="light" onClick={handleClose} className="px-4">
                            <i className="ri-close-line me-1"></i>
                            Close
                        </Button>
                        <Button
                            color="primary"
                            onClick={() => {
                                if (activeTab === '1') {
                                    // Reset to first page and fetch fresh data
                                    setFilters({ page: 1, limit: filters.limit || 20 });
                                    fetchHistory({ page: 1, limit: filters.limit || 20 });
                                } else if (activeTab === '2') {
                                    // Reset to first page and fetch fresh data
                                    setStatementFilters({ page: 1, limit: statementFilters.limit || 20, type: 'all' });
                                    fetchStatement({ page: 1, limit: statementFilters.limit || 20 });
                                }
                            }}
                            disabled={loading || statementLoading}
                            className="px-4"
                        >
                            <i className="ri-refresh-line me-1"></i>
                            Refresh
                        </Button>
                    </div>
                </div>
            </ModalFooter>
        </Modal>
    );
};

export default WithdrawalHistoryModal;

